import React from 'react';
import IDraftData from '../../types/DraftData';
import DraftRow from './DraftRow';
import { useCarrierData } from '../../hooks/useCarrierData';
import { useFlightTypeDescriptions } from '../../hooks/useFlightTypeDescriptions';
import IReplacementRange from '../../types/ReplacementRange';

interface DraftGroupProps {
  draft: IDraftData[];
  onEditDraft;
  onRemoveDraft;
}

/**
 * Draft table component with high cohesion
 * Handles table structure and accessibility features
 */
const DraftGroup: React.FC<DraftGroupProps> = ({ 
  draft,
  onEditDraft,
  onRemoveDraft
}) => {
  const { getCarrierCodeById } = useCarrierData();
  const { flightTypeOptions, isLoading } = useFlightTypeDescriptions();

  if (isLoading) {
    return (
      <div 
        className="draft-table__loading"
        aria-live="polite"
        aria-label="Loading drafts"
      >
        <div className="loading-spinner" aria-hidden="true"></div>
        <span>Loading draft...</span>
      </div>
    );
  }

  const sendDraftIdToRemove = () => {
    onRemoveDraft(draft[0].id); // Calling the parent's callback with the data
  };

  const sendDraftToEdit = () => {
    onEditDraft(mappedDraft, draft[0].id); // Calling the parent's callback with the data
  };

  const mappedDraft: IReplacementRange[] = draft.map(range => ({
    id: range.flightRangeId.toString(),
    start: range.flightRangeStart,
    end: range.flightRangeStop,
    marketingCarrierId: range.marketingCarrierId,
    marketingCarrier: getCarrierCodeById(range.marketingCarrierId)?.toString() ?? '',
    operatingCarrierId: range.operatingCarrierId,
    operatingCarrier: getCarrierCodeById(range.operatingCarrierId)?.toString() ?? '',
    flightTypeId: range.flightTypeId.toString(),
    flightType: flightTypeOptions.find(option => Number(option.id) === range.flightTypeId)?.value ?? '',
    flightTypeDescription: flightTypeOptions.find(option => Number(option.id) === range.flightTypeId)?.value ?? '',
    effectiveDate: range.effectiveDate,
    discontinueDate: range.discontinueDate,
    wetLease: range.wetLease
  }))

  return (
    <div className="draft-table__wrapper">
      <div className="draft-group__title-container">
        <div className="draft-group__title-container">
          <div className="draft-group__title">
            DRAFT ID: {draft[0].id}
          </div>
          <div className="draft-group__subtitle">
            Modified on {draft[0].modifiedDate} by {draft[0].modifiedBy}
          </div>
        </div>
        <div className="draft-group__title-container">
          <div aria-sort="none">
            <button
              className="draft-group__title-link"
              onClick={sendDraftToEdit}
              type="button"
            >
              Edit
            </button>
          </div>
          <div aria-sort="none">
            <button
              className="draft-group__title-link"
              onClick={sendDraftIdToRemove}
              type="button"
            >
              Remove
            </button>
          </div>
        </div>
      </div>
      <table 
        className="draft-table"
        aria-label="Flight Range Draft"
        aria-rowcount={draft.length + 1} // +1 for header
      >
        <thead>
          <tr>
            <th scope="col" aria-sort="none"></th>
            <th scope="col" aria-sort="none">MKT. CARRIER</th>
            <th scope="col" aria-sort="none">FLIGHT RANGE</th>
            <th scope="col" aria-sort="none">OP. CARRIER</th>
            <th scope="col" aria-sort="none">FLIGHT TYPE</th>
            <th scope="col" aria-sort="none">EFFECTIVE</th>
            <th scope="col" aria-sort="none">DISCONTINUED</th>
          </tr>
        </thead>
        <tbody>
          {mappedDraft.map((draftRange, index) => (
            <DraftRow 
              key={`${draftRange.id}-${draft[0].modifiedDate}`} 
              draftRange={draftRange}
              aria-rowindex={index + 2} // +2 because header is row 1
            />
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default DraftGroup;